import { formatMinutesToTimeFormat } from "@components/inputs/date/utils";
import { isNotDefined } from "@utils/general";
import i18next from "i18next";
import React, { Fragment, KeyboardEvent, MouseEvent, PureComponent, ReactElement } from "react";

import { IconSize } from "../../enums";
import TestIds from "../../testIds";
import { IFocusableItemProps } from "../focusManager";
import { FieldValidationWarningIcon } from "../icon";
import TextEllipsis from "../textEllipsis/TextEllipsis";
import Tooltip from "../tooltip";
import {
    Action,
    ActionsRow,
    ActionsWrapper,
    ActionTitle,
    Circle,
    DayHeaderWrapper,
    DayIndex,
    DayWrapper,
    MoreButton,
    WorkTimeWrapper
} from "./Calendar.styles";
import { IDayAction, TileSize } from "./Calendar.utils";

interface IProps {
    dayNumber: number;
    isMonday: boolean;
    isSelected: boolean;
    isToday: boolean;
    isHoliday: boolean;
    isFocused: boolean;
    onMouseDown: (e: MouseEvent, dayNumber: number) => void;
    onMouseEnter: (e: MouseEvent, dayNumber: number) => void;
    onMouseUp: (e: MouseEvent, dayNumber: number) => void;
    onKeyDown: (e: KeyboardEvent, dayNumber: number) => void;
    onKeyUp: (e: KeyboardEvent, dayNumber: number) => void;
    focusableProps: IFocusableItemProps;
    actions?: IDayAction[];
    placeholders?: IDayAction[];
    size?: number;
    workedMinutes?: number;
    isDisabled?: boolean;
}

const ACTIONS_TO_SHOW = 3;

export default class Day extends PureComponent<IProps> {
    get isBig(): boolean {
        return this.props.size >= TileSize.BREAK;
    }

    handleMouseDown = (e: MouseEvent): void => {
        if (e.button !== 0) { // only left click
            return;
        }
        this.props.onMouseDown?.(e, this.props.dayNumber);
    };

    handleMouseUp = (e: MouseEvent): void => {
        if (e.button !== 0) { // only left click
            return;
        }
        e.preventDefault();
        this.props.onMouseUp?.(e, this.props.dayNumber);
    };

    handleMouseEnter = (e: MouseEvent): void => {
        e.preventDefault();
        this.props.onMouseEnter?.(e, this.props.dayNumber);
    };

    handleKeyDown = (e: KeyboardEvent): void => {
        this.props.onKeyDown?.(e, this.props.dayNumber);
    };

    handleKeyUp = (e: KeyboardEvent): void => {
        this.props.onKeyUp?.(e, this.props.dayNumber);
    };

    renderActionContent = (action: IDayAction, hideTitle: boolean): ReactElement => {
        if (action.hasConflict) {
            return <FieldValidationWarningIcon width={IconSize.S} height={IconSize.S} />;
        }

        return <>
            <Circle $isHalf={!action.isFullDay} data-testid={TestIds.Circle} />
            {!hideTitle && <ActionTitle>
                <TextEllipsis text={action.title} />
            </ActionTitle>}
        </>;
    };

    renderWorkedMinutes = (): ReactElement => {
        const workedMinutes = this.props.workedMinutes;
        if (isNotDefined(workedMinutes) || workedMinutes === 0) {
            return null;
        }
        return <WorkTimeWrapper
            data-testid={TestIds.CalendarDayWorkTime}>
            {formatMinutesToTimeFormat(workedMinutes)}&nbsp;{i18next.t("Components:Calendar.HourPlaceholder")}
        </WorkTimeWrapper>;
    }

    render() {
        let actions = this.props.actions ?? [];
        const placeholders = this.props.placeholders ?? [];

        if (!this.isBig) {
            actions = [...actions, ...placeholders];
        } else {
            // placeholders could be set in separate prop or together with actions with flag -> separate them
            placeholders.push(...actions.filter(action => action.isPlaceholder));
            actions = actions.filter(action => !action.isPlaceholder);
        }
        const actionsCnt = actions?.length ?? 0;
        const hasMoreActions = actionsCnt > 1;
        const actionsToRender = actions?.filter(action => {
            return !!action.title;
        })?.sort((a, b) => a.order - b.order).slice(0, ACTIONS_TO_SHOW);
        const tooltipActions = actions?.slice(ACTIONS_TO_SHOW);
        return <DayWrapper
            $isDisabled={this.props.isDisabled}
            data-focusable-item-disabled={!!this.props.isDisabled}
                $isSelected={this.props.isSelected}
                $isHoliday={this.props.isHoliday}
                $isFocused={this.props.isFocused}
                onMouseDown={this.handleMouseDown}
                onMouseEnter={this.handleMouseEnter}
                onMouseUp={this.handleMouseUp}
                onKeyDown={this.handleKeyDown}
                onKeyUp={this.handleKeyUp}
                data-testid={TestIds.CalendarDay}
                data-selected={this.props.isSelected ? "true" : undefined}
                {...this.props.focusableProps}
        >
            <DayHeaderWrapper>
                <DayIndex $isToday={this.props.isToday}
                    data-testid={TestIds.CalendarDayNumber}>
                    {this.props.dayNumber}
                </DayIndex>
                {this.renderWorkedMinutes()}
            </DayHeaderWrapper>
            <ActionsWrapper data-testid={TestIds.CalendarDayAbsences}>
                <ActionsRow key={"placeholders"}>
                    {placeholders?.map((action, index) => {
                        const hideTitle = !action.isStart || action.hasConflict || hasMoreActions || action.hideTitle;
                        return <Tooltip key={action.title + index} content={action.title} isHidden={!hideTitle}>
                            {(ref) => <Action
                                data-testid={TestIds.CalendarDayAbsence}
                                key={action.title + index}
                                ref={ref}
                                $color={action.color ?? "C_CHART_orange"}
                                $isPlaceholder={action.isPlaceholder}
                                $isMonday={this.props.isMonday}
                                $isStart={action.isStart}
                                $isEnd={action.isEnd}
                                $hasConflict={action.hasConflict}
                                $dayHasMoreActions={false}
                            >
                                {this.renderActionContent(action, hideTitle)}
                            </Action>
                            }
                        </Tooltip>;
                    })}
                </ActionsRow>
                <ActionsRow key={"actions"}>
                    {actionsToRender.map((action, index) => {
                        const hideTitle = !action.isStart || action.hasConflict || hasMoreActions || action.hideTitle;
                        return <Tooltip key={action.title + index} content={action.title} isHidden={!hideTitle}>
                            {(ref) => <Action
                                data-testid={TestIds.CalendarDayAbsence}
                                key={action.title + index}
                                ref={ref}
                                $color={action.color}
                                $isStart={action.isStart || actionsCnt !== 1}
                                $isEnd={action.isEnd || actionsCnt !== 1}
                                $isPlaceholder={action.isPlaceholder}
                                $hasConflict={action.hasConflict}
                                $dayHasMoreActions={actionsCnt > 1}
                            >
                                {this.renderActionContent(action, hideTitle)}
                            </Action>
                            }
                        </Tooltip>;
                    })}
                    {!!tooltipActions?.length && <Tooltip content={<>
                        {tooltipActions.map(action => <Fragment key={action.title}>{`${action.title}\n`}</Fragment>)}
                    </>}>
                        {ref => <MoreButton ref={ref}>
                            {`+${tooltipActions.length}`}
                        </MoreButton>}
                    </Tooltip>}
                </ActionsRow>
            </ActionsWrapper>
        </DayWrapper>;
    }
}